import {
  incomingDocSchema,
  paginatedSchema,
  surveyWithLinesSchema,
  type IncomingDocDto,
  type Paginated,
  surveySchema,
  type DocLocale,
  type SurveyDto,
  type SurveyWithLinesDto,
  type SurveyStatus,
} from '@eombor/contracts';
import { apiClient } from '../../auth/model/auth.store.js';
import type { ApiClient } from '../../../shared/api/client.js';

/**
 * Buxgalteriya hujjatlari — so'rovnoma (`S-…`) va prixod (`X-…`). Manba:
 *   `accounting/infrastructure/http/survey.controller.ts`
 *   `accounting/infrastructure/http/incoming-doc.controller.ts`
 *
 * Bu yerda faqat O'QISH bor va bu ataylab: hujjat talabnoma o'tishida tug'iladi,
 * shuning uchun na yaratish, na tasdiqlash endpointi mavjud. Buxgalterning
 * "Tasdiqlash" tugmasi ham aslida talabnoma o'tishi (`ACCOUNTING_APPROVED`) —
 * ikkinchi yo'l ochilsa, hujjat talabnoma holatidan ajralib ketardi.
 */

const surveyPageSchema = paginatedSchema(surveySchema);

/**
 * Ro'yxatdagi prixodda SATRLAR yo'q — ular faqat bitta hujjat so'ralganda
 * keladi (`IncomingDocRow` va `IncomingDocWithLines` farqi). Turni "satrlar
 * bor" deb ko'rsatish yolg'on shartnoma bo'lardi.
 */
const incomingDocRowSchema = incomingDocSchema.omit({ lines: true });

export type IncomingDocRowDto = Omit<IncomingDocDto, 'lines'>;

const incomingDocPageSchema = paginatedSchema(incomingDocRowSchema);

export interface SurveyQuery {
  status?: SurveyStatus;
  requisitionId?: string;
  page?: number;
  limit?: number;
}

export interface IncomingDocQuery {
  orgUnitId?: string;
  requisitionId?: string;
  page?: number;
  limit?: number;
}

/** Bo'sh qiymat so'rovga QO'SHILMAYDI — server uni baribir tekshiruvga tashlaydi. */
const queryString = (query: SurveyQuery & IncomingDocQuery): string => {
  const params = new URLSearchParams({
    page: String(query.page ?? 1),
    limit: String(query.limit ?? 20),
  });
  if (query.status !== undefined) params.set('status', query.status);
  if (query.orgUnitId !== undefined && query.orgUnitId !== '') {
    params.set('orgUnitId', query.orgUnitId);
  }
  if (query.requisitionId !== undefined && query.requisitionId !== '') {
    params.set('requisitionId', query.requisitionId);
  }
  return params.toString();
};

export interface AccountingApi {
  listSurveys: (query?: SurveyQuery) => Promise<Paginated<SurveyDto>>;
  survey: (id: string) => Promise<SurveyWithLinesDto>;
  /**
   * Blankdagi «ТРЕБОВАНИЕ №» — buxgalter o'z daftaridan yozadi.
   *
   * `docNo` ga TEGMAYDI: u tizimning ichki raqami va unga havolalar,
   * jurnal hamda qidiruv tayanadi. Faqat `PENDING` so'rovnomada mumkin.
   */
  setSurveyFormNo: (id: string, formNo: string | null) => Promise<SurveyWithLinesDto>;
  surveyPdf: (id: string) => Promise<Blob>;
  /** TREBOVANIYA — ФМУ №25 blanki. Til serverga so'rov bilan boradi. */
  requirementPdf: (id: string, locale: DocLocale) => Promise<Blob>;
  listDocs: (query?: IncomingDocQuery) => Promise<Paginated<IncomingDocRowDto>>;
  doc: (id: string) => Promise<IncomingDocDto>;
  docPdf: (id: string) => Promise<Blob>;
}

const surveyPath = (id: string): string => `/surveys/${encodeURIComponent(id)}`;
const docPath = (id: string): string => `/incoming-docs/${encodeURIComponent(id)}`;

export const createAccountingApi = (client: ApiClient): AccountingApi => ({
  listSurveys: (query = {}) =>
    client.apiFetch(`/surveys?${queryString(query)}`, { schema: surveyPageSchema }),

  survey: (id) => client.apiFetch(surveyPath(id), { schema: surveyWithLinesSchema }),

  setSurveyFormNo: (id, formNo) =>
    client.apiFetch(surveyPath(id), {
      method: 'PATCH',
      body: JSON.stringify({ formNo }),
      schema: surveyWithLinesSchema,
    }),

  /*
   * PDF `apiBlob` bilan olinadi, imzolangan havola bilan EMAS: `<a href>`
   * ochilganda `Authorization` sarlavhasi ketmaydi va tokenni manzilga
   * yozishga to'g'ri kelardi — u esa tarixda va jurnalda qolib ketardi.
   */
  surveyPdf: (id) => client.apiBlob(`${surveyPath(id)}/pdf`),

  /*
   * Blank SERVERDA yasaladi va ilova tili faqat mijozda ma'lum — shuning
   * uchun til so'rov bilan ketadi. Brauzerda chizilgan hujjatning mazmuni
   * mijoz qo'lida bo'lardi va uni rasmiy deb hisoblab bo'lmasdi.
   */
  requirementPdf: (id, locale) =>
    client.apiBlob(`${surveyPath(id)}/requirement.pdf?locale=${encodeURIComponent(locale)}`),

  listDocs: (query = {}) =>
    client.apiFetch(`/incoming-docs?${queryString(query)}`, { schema: incomingDocPageSchema }),

  doc: (id) => client.apiFetch(docPath(id), { schema: incomingDocSchema }),

  docPdf: (id) => client.apiBlob(`${docPath(id)}/pdf`),
});

export const accountingApi = createAccountingApi(apiClient);

/** Fayl nomi hujjat RAQAMIDAN — hisobchining papkasida `X-2026-0007.pdf` bo'lib yotadi. */
export const docFileName = (docNo: string): string => `${docNo}.pdf`;
